import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Save, Plus, X, Disc3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import LocationAutocomplete from '../components/LocationAutocomplete';
import ImageUpload from '../components/ImageUpload';

import { API_URL } from '../utils/api';

const genres = ['Country', 'Western Swing', 'Honky Tonk', 'Traditional Country', 'Country Pop', 'Line Dance Classics', 'Two-Step', 'Mixed/Top 40'];
const services = ['Weddings', 'Private Parties', 'Corporate Events', 'Dance Halls', 'Festivals', 'Bar/Club Nights', 'Lessons/Workshops', 'MC Services'];

export default function AddEditDJPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    stage_name: '',
    bio: '',
    genres: [],
    services: [],
    location: '',
    phone: '',
    email: '',
    website: '',
    social_links: { instagram: '', facebook: '', twitter: '' },
    image_url: '',
    sample_mix_url: ''
  });

  useEffect(() => {
    if (isEdit) {
      fetchDJ();
    }
  }, [id]);

  const fetchDJ = async () => {
    try {
      const response = await fetch(`${API_URL}/api/djs/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Handle legacy data: if no location field, build from location_city/location_state
        if (!data.location && (data.location_city || data.location_state)) {
          data.location = [data.location_city, data.location_state].filter(Boolean).join(', ');
        }
        setFormData({
          ...data,
          social_links: data.social_links || { instagram: '', facebook: '', twitter: '' }
        });
      }
    } catch (error) {
      console.error('Error fetching DJ:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit ? `${API_URL}/api/djs/${id}` : `${API_URL}/api/djs`;
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEdit ? 'DJ profile updated!' : 'DJ profile created!');
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save DJ profile');
      }
    } catch (error) {
      toast.error('Error saving DJ profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="add-edit-dj-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-700 to-purple-600 text-white">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Disc3 className="w-6 h-6" />
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {isEdit ? 'Edit DJ Profile' : 'Create DJ Profile'}
                </h1>
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-white text-violet-600 hover:bg-slate-100"
              data-testid="save-dj-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-8">
          {/* Basic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Basic Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                  className="mt-1.5"
                  data-testid="dj-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="stage_name">Stage Name / DJ Name</Label>
                <Input
                  id="stage_name"
                  name="stage_name"
                  value={formData.stage_name}
                  onChange={handleChange}
                  placeholder="e.g., DJ Country Thunder"
                  className="mt-1.5"
                  data-testid="dj-stage-name-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="bio">Bio *</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell folks about yourself and your DJ style..."
                  required
                  rows={4}
                  className="mt-1.5"
                  data-testid="dj-bio-input"
                />
              </div>
            </div>
          </motion.div>

          {/* Profile Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Profile Image</h2>
            <ImageUpload
              value={formData.image_url}
              onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              label="Upload Profile Photo"
              helpText="Upload a professional photo or your DJ setup"
            />
          </motion.div>

          {/* Location */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Location</h2>
            
            <div>
              <Label htmlFor="location">Location *</Label>
              <div className="mt-1.5">
                <LocationAutocomplete
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                  placeholder="Start typing a UK location..."
                  required
                />
              </div>
              <p className="text-sm text-slate-400 mt-1.5">
                Start typing to see suggestions, or enter a custom location
              </p>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Contact & Social</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="mt-1.5"
                  data-testid="dj-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="dj@example.com"
                  className="mt-1.5"
                  data-testid="dj-email-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.yoursite.com"
                  className="mt-1.5"
                  data-testid="dj-website-input"
                />
              </div>

              <div>
                <Label>Instagram URL</Label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="mt-1.5"
                />
              </div>
              
              <div>
                <Label>Facebook URL</Label>
                <Input
                  value={formData.social_links.facebook}
                  onChange={(e) => handleSocialChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="mt-1.5"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="sample_mix_url">Sample Mix URL (SoundCloud, Mixcloud, etc.)</Label>
                <Input
                  id="sample_mix_url"
                  name="sample_mix_url"
                  value={formData.sample_mix_url}
                  onChange={handleChange}
                  placeholder="https://soundcloud.com/..."
                  className="mt-1.5"
                  data-testid="dj-mix-input"
                />
              </div>
            </div>
          </motion.div>

          {/* Genres & Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Music & Services</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="mb-3 block">Music Genres</Label>
                <div className="flex flex-wrap gap-3">
                  {genres.map(genre => (
                    <label key={genre} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.genres.includes(genre)}
                        onCheckedChange={() => handleGenreToggle(genre)}
                      />
                      <span>{genre}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="mb-3 block">Services Offered</Label>
                <div className="flex flex-wrap gap-3">
                  {services.map(service => (
                    <label key={service} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.services.includes(service)}
                        onCheckedChange={() => handleServiceToggle(service)}
                      />
                      <span>{service}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
